import { XIcon } from '@phosphor-icons/react';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import FilterSelect from '@/components/ui/filter-select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { showToast } from '@/components/ui/toast';
import { useGlobalState } from '@/contexts/GlobalStateContext';
import useRequest from '@/hooks/use-request';

interface SendNotificationFormData {
  recipient: 'all' | 'role' | 'users';
  role?: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'success' | 'error';
}

interface User {
  _id: string;
  username: string;
  email: string;
  role: string;
}

interface Role {
  _id: string;
  name: string;
}

const SendNotificationSheet: React.FC = () => {
  const { hideSheet } = useGlobalState();
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);

  const userToken = localStorage.getItem('token') || '';

  // API request hooks
  const { makeRequest: getUsers } = useRequest('/users', 'GET', {
    Authorization: `Bearer ${userToken}`,
  });

  const { makeRequest: getRoles } = useRequest('/roles', 'GET', {
    Authorization: `Bearer ${userToken}`,
  });

  const { makeRequest: sendToAll } = useRequest('/notifications/all', 'POST', {
    Authorization: `Bearer ${userToken}`,
  });

  const { makeRequest: sendToRoles } = useRequest(
    '/notifications/roles',
    'POST',
    {
      Authorization: `Bearer ${userToken}`,
    }
  );

  const { makeRequest: sendToUsers } = useRequest(
    '/notifications/users',
    'POST',
    {
      Authorization: `Bearer ${userToken}`,
    }
  );

  const { control, handleSubmit, watch, reset } =
    useForm<SendNotificationFormData>({
      defaultValues: {
        recipient: 'all',
        title: '',
        message: '',
        type: 'info',
      },
    });

  const selectedRecipient = watch('recipient');

  // Fetch users and roles on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersResponse] = await getUsers();
        if (usersResponse.status) {
          setAvailableUsers(usersResponse.data?.users || []);
        }

        const [rolesResponse] = await getRoles();
        if (rolesResponse.status) {
          setAvailableRoles(rolesResponse.data || []);
        }
      } catch (error) {}
    };

    fetchData();
  }, []);

  // Filter users based on search
  const filteredUsers = availableUsers.filter(
    user =>
      user.username.toLowerCase().includes(userSearch.toLowerCase()) ||
      user.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  const addUser = useCallback(
    (userId: string) => {
      if (!selectedUsers.includes(userId)) {
        setSelectedUsers(prev => [...prev, userId]);
      }
      setUserSearch('');
    },
    [selectedUsers]
  );

  const removeUser = useCallback((userId: string) => {
    setSelectedUsers(prev => prev.filter(id => id !== userId));
  }, []);

  const handleClose = () => {
    reset();
    setSelectedUsers([]);
    setUserSearch('');
    hideSheet('send-notification');
  };

  const onSubmit = handleSubmit(async formData => {
    setLoading(true);
    try {
      const payload = {
        title: formData.title.trim(),
        message: formData.message.trim(),
        type: formData.type,
      };

      let response;

      switch (formData.recipient) {
        case 'all':
          [response] = await sendToAll(payload);
          break;
        case 'role':
          if (!formData.role) {
            showToast('Please select a role', false, { position: 'top-right' });
            return;
          }
          [response] = await sendToRoles({
            ...payload,
            roles: formData.role,
          });
          break;
        case 'users':
          if (selectedUsers.length === 0) {
            showToast('Please select at least one user', false, {
              position: 'top-right',
            });
            return;
          }
          [response] = await sendToUsers({
            ...payload,
            userIds: selectedUsers,
          });
          break;
      }

      if (response?.status) {
        showToast(response.message || 'Notification sent successfully', true, {
          position: 'top-right',
        });
        handleClose();
      } else {
        showToast(response?.message || 'Failed to send notification', false, {
          position: 'top-right',
        });
      }
    } catch (error) {
      showToast('Failed to send notification', false, {
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  });

  const recipientOptions = [
    { value: 'all', label: 'All Users' },
    { value: 'role', label: 'By Role' },
    { value: 'users', label: 'Specify User(s)' },
  ];

  const roleOptions = availableRoles.map(role => ({
    value: role.name,
    label:
      role.name.charAt(0).toUpperCase() + role.name.slice(1).replace(/-/g, ' '),
  }));

  const typeOptions = [
    { value: 'info', label: 'Info' },
    { value: 'success', label: 'Success' },
    { value: 'warning', label: 'Warning' },
    { value: 'error', label: 'Error' },
  ];

  return (
    <div className='space-y-6'>
      <form onSubmit={onSubmit} className='space-y-4'>
        {/* Recipient Selection */}
        <Controller
          name='recipient'
          control={control}
          rules={{ required: 'Recipient is required' }}
          render={({ field, fieldState }) => (
            <FilterSelect
              name='recipient'
              value={field.value}
              options={recipientOptions}
              label='Recipient'
              placeholder='Select recipient type'
              error={fieldState?.error?.message}
              onChange={field.onChange}
              className='w-full'
            />
          )}
        />

        {/* Role Selection - Only show if recipient is 'role' */}
        {selectedRecipient === 'role' && (
          <Controller
            name='role'
            control={control}
            rules={{ required: 'Role is required' }}
            render={({ field, fieldState }) => (
              <FilterSelect
                name='role'
                value={field.value || ''}
                options={roleOptions}
                label='Role'
                placeholder='Select role'
                error={fieldState?.error?.message}
                onChange={field.onChange}
                className='w-full'
              />
            )}
          />
        )}

        {/* User Selection - Only show if recipient is 'users' */}
        {selectedRecipient === 'users' && (
          <div className='space-y-2'>
            <Label>Select Users</Label>
            <div className='space-y-2'>
              <Input
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
                placeholder='Search users by name or email...'
                className='w-full'
              />

              {/* User Search Results */}
              {userSearch && filteredUsers.length > 0 && (
                <div className='max-h-40 overflow-y-auto border rounded-md bg-white'>
                  {filteredUsers.slice(0, 10).map(user => (
                    <div
                      key={user._id}
                      className='p-2 hover:bg-gray-5 cursor-pointer border-b last:border-b-0'
                      onClick={() => addUser(user._id)}
                    >
                      <div className='text-sm font-medium'>{user.username}</div>
                      <div className='text-xs text-gray-50'>{user.email}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Selected Users */}
              {selectedUsers.length > 0 && (
                <div className='space-y-2'>
                  <Label className='text-sm text-gray-60'>
                    Selected Users:
                  </Label>
                  <div className='flex flex-wrap gap-2'>
                    {selectedUsers.map(userId => {
                      const user = availableUsers.find(u => u._id === userId);
                      return (
                        <Badge
                          key={userId}
                          variant='secondary'
                          className='flex items-center gap-1'
                        >
                          {user?.username || userId}
                          <button
                            type='button'
                            onClick={() => removeUser(userId)}
                            className='ml-1 hover:text-destructive-50'
                          >
                            <XIcon className='h-3 w-3' />
                          </button>
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Notification Type */}
        <Controller
          name='type'
          control={control}
          rules={{ required: 'Type is required' }}
          render={({ field, fieldState }) => (
            <FilterSelect
              name='type'
              value={field.value}
              options={typeOptions}
              label='Type'
              placeholder='Select notification type'
              error={fieldState?.error?.message}
              onChange={field.onChange}
              className='w-full'
            />
          )}
        />

        {/* Title */}
        <Controller
          name='title'
          control={control}
          rules={{
            required: 'Title is required',
            minLength: {
              value: 3,
              message: 'Title must be at least 3 characters',
            },
            maxLength: {
              value: 100,
              message: 'Title must not exceed 100 characters',
            },
          }}
          render={({ field, fieldState }) => (
            <Input
              value={field.value}
              onChange={field.onChange}
              placeholder='Enter notification title'
              label='Title'
              error={fieldState?.error?.message}
              className='w-full'
            />
          )}
        />

        {/* Message */}
        <Controller
          name='message'
          control={control}
          rules={{
            required: 'Message is required',
            minLength: {
              value: 10,
              message: 'Message must be at least 10 characters',
            },
            maxLength: {
              value: 500,
              message: 'Message must not exceed 500 characters',
            },
          }}
          render={({ field, fieldState }) => (
            <div className='space-y-2'>
              <Label htmlFor='message'>Message</Label>
              <Textarea
                id='message'
                value={field.value}
                onChange={field.onChange}
                placeholder='Enter notification message'
                className='w-full'
                rows={4}
              />
              {fieldState?.error?.message && (
                <p className='text-sm text-destructive-60'>
                  {fieldState.error.message}
                </p>
              )}
            </div>
          )}
        />

        {/* Action Buttons */}
        <div className='flex justify-end gap-2 pt-4'>
          <Button type='button' hierarchy='outlined' onClick={handleClose}>
            Cancel
          </Button>
          <Button type='submit' disabled={loading}>
            {loading ? 'Sending...' : 'Send Notification'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SendNotificationSheet;
